import {
  REPORT_CANVAS_COLUMNS,
  REPORT_CANVAS_MAX_GRID_ROW,
  REPORT_CANVAS_MAX_ITEM_HEIGHT,
} from '@erp/shared-types';
import type { CSSProperties } from 'react';
import type { GridGeometry, GridRect } from './types';

// ── The Report grid ───────────────────────────────────────────────────────────
// Every canvas lays its components on REPORT_CANVAS_COLUMNS columns and rows
// of GRID_ROW_HEIGHT pixels, GRID_GAP apart. Each item has a place and size
// in grid cells ({ x, y, w, h }) and the grid is compacted upward: every item
// sits as high as the items above it let it (gravity), so the only holes are
// beside items, never above them. Pure: rects in, rects out.

/** The height of one grid row, in pixels. */
export const GRID_ROW_HEIGHT = 40;
/** The space between two columns or two rows, in pixels. */
export const GRID_GAP = 8;
/** One grid row and the gap beneath it. */
export const GRID_ROW_PITCH = GRID_ROW_HEIGHT + GRID_GAP;

/** The pixel height `rows` grid rows take. */
export function gridRowsHeight(rows: number): number {
  return Math.max(0, rows * GRID_ROW_PITCH - GRID_GAP);
}

/** The first grid row beneath every item (0 for none). */
export function gridBottom(rects: readonly GridRect[]): number {
  return rects.reduce((bottom, rect) => Math.max(bottom, rect.y + rect.h), 0);
}

export function rectsOverlap(a: GridRect, b: GridRect): boolean {
  return (
    a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h
  );
}

/** `rect` kept within the grid: at least 1×1, inside its columns, from row 0. */
export function clampRect<R extends GridRect>(rect: R): R {
  const w = Math.min(REPORT_CANVAS_COLUMNS, Math.max(1, Math.round(rect.w)));
  const h = Math.min(
    REPORT_CANVAS_MAX_ITEM_HEIGHT,
    Math.max(1, Math.round(rect.h)),
  );
  return {
    ...rect,
    w,
    h,
    x: Math.min(REPORT_CANVAS_COLUMNS - w, Math.max(0, Math.round(rect.x))),
    y: Math.min(REPORT_CANVAS_MAX_GRID_ROW, Math.max(0, Math.round(rect.y))),
  };
}

/**
 * Compacts rects upward, in reading order (top to bottom, then left to
 * right; `first` goes first among rects that start on the same cell row):
 * each rises while the cell above is free, then drops below anything it
 * still overlaps. So overlapping rects are pushed down, and an item moved or
 * resized onto others (`first`) keeps its place while they make room. Keeps
 * the input's order.
 */
export function compactRects<R extends GridRect & { id: string }>(
  rects: readonly R[],
  first?: string,
): R[] {
  const order = [...rects].sort(
    (a, b) =>
      a.y - b.y || Number(b.id === first) - Number(a.id === first) || a.x - b.x,
  );
  const placed: R[] = [];
  const at = new Map<string, R>();
  for (const rect of order) {
    const collides = (y: number) =>
      placed.some((other) => rectsOverlap({ ...rect, y }, other));
    let y = rect.y;
    while (y > 0 && !collides(y - 1)) y--;
    while (collides(y)) y++;
    const next = rect.y === y ? rect : { ...rect, y };
    placed.push(next);
    at.set(rect.id, next);
  }
  return rects.map((rect) => at.get(rect.id) ?? rect);
}

/**
 * Where a `w`×`h` item added at the bottom goes: the first free spot, left to
 * right, on the row the last items start on, else a new row beneath them.
 */
export function bottomSpot(
  rects: readonly GridRect[],
  size: { w: number; h: number },
): { x: number; y: number } {
  const w = Math.min(REPORT_CANVAS_COLUMNS, size.w);
  const lastRow = rects.reduce((row, rect) => Math.max(row, rect.y), 0);
  for (let x = 0; x + w <= REPORT_CANVAS_COLUMNS; x++) {
    const spot = { x, y: lastRow, w, h: size.h };
    if (!rects.some((rect) => rectsOverlap(spot, rect))) return spot;
  }
  return { x: 0, y: gridBottom(rects) };
}

/**
 * Cuts the grid into bands, top to bottom: runs of grid rows no item crosses
 * the edge of, so a band can be laid out (or printed) on its own without
 * splitting a component. Each band's rects are moved to start at row 0; `top`
 * is the grid row it started on.
 */
export function gridBands<R extends GridRect>(
  rects: readonly R[],
): { top: number; rects: R[] }[] {
  const order = [...rects].sort((a, b) => a.y - b.y || a.x - b.x);
  const bands: { top: number; bottom: number; rects: R[] }[] = [];
  for (const rect of order) {
    const band = bands.at(-1);
    if (band && rect.y < band.bottom) {
      band.rects.push(rect);
      band.bottom = Math.max(band.bottom, rect.y + rect.h);
    } else bands.push({ top: rect.y, bottom: rect.y + rect.h, rects: [rect] });
  }
  return bands.map(({ top, rects: banded }) => ({
    top,
    rects: banded.map((rect) => ({ ...rect, y: rect.y - top })),
  }));
}

// ── Pointer → grid cell ───────────────────────────────────────────────────────

/** The pixels from one column's left edge to the next's, on a grid `width` wide. */
export function columnPitch(width: number): number {
  return (width + GRID_GAP) / REPORT_CANVAS_COLUMNS;
}

/**
 * Where a drag lands, snapped to the grid: an item moved from `from` goes as
 * many cells as the pointer moved (`delta`); a new `size`d one from the
 * palette is centered on the pointer (as the drag preview is).
 */
export function dropCell(
  geometry: GridGeometry,
  drag:
    | { from: GridRect; delta: { x: number; y: number } }
    | { size: { w: number; h: number }; pointer: { x: number; y: number } },
): GridRect {
  const pitch = columnPitch(geometry.width);
  if ('from' in drag)
    return clampRect({
      ...drag.from,
      x: drag.from.x + Math.round(drag.delta.x / pitch),
      y: drag.from.y + Math.round(drag.delta.y / GRID_ROW_PITCH),
    });
  const { w, h } = drag.size;
  return clampRect({
    w,
    h,
    x: Math.round((drag.pointer.x - geometry.left) / pitch - w / 2),
    y: Math.round((drag.pointer.y - geometry.top) / GRID_ROW_PITCH - h / 2),
  });
}

// ── Styles ────────────────────────────────────────────────────────────────────

/** The grid: 12 equal columns, rows of the unit height. */
export const GRID_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: `repeat(${REPORT_CANVAS_COLUMNS}, minmax(0, 1fr))`,
  gridAutoRows: GRID_ROW_HEIGHT,
  gap: GRID_GAP,
};

/** An item's place on the grid. */
export function gridItemStyle(rect: GridRect): CSSProperties {
  return {
    gridColumn: `${rect.x + 1} / span ${rect.w}`,
    gridRow: `${rect.y + 1} / span ${rect.h}`,
    minWidth: 0,
  };
}

// ── Layouts saved before the grid ─────────────────────────────────────────────

/** `count` equal shares of `columns`, the larger shares first (5 of 12 → 3,3,2,2,2). */
function equalSpans(count: number, columns: number): number[] {
  const share = Math.floor(columns / count);
  const rest = columns - share * count;
  return Array.from({ length: count }, (_, index) =>
    Math.max(1, share + (index < rest ? 1 : 0)),
  );
}

/**
 * The columns a saved row's cells spanned. A cell with a span kept it; cells
 * without one shared the columns the others left. When those don't fit, the
 * whole row split equally — as a row saved before spans did.
 */
export function rowSpans(spans: readonly (number | undefined)[]): number[] {
  const fixed = spans.reduce<number>((total, span) => total + (span ?? 0), 0);
  const open = spans.filter((span) => span === undefined).length;
  const free = REPORT_CANVAS_COLUMNS - fixed;
  if (fixed > REPORT_CANVAS_COLUMNS || free < open)
    return equalSpans(spans.length, REPORT_CANVAS_COLUMNS);
  if (open === 0) return spans.map((span) => span ?? 0);
  const shares = equalSpans(open, free);
  let next = 0;
  return spans.map((span) => span ?? shares[next++] ?? 1);
}

/**
 * A layout saved as rows onto the grid: row by row, each cell as wide as it
 * spanned, beside the one before it; each row as tall as its tallest
 * component's default height (`heightOf`), beneath the row before it.
 */
export function rowsToGrid<C extends { span?: number }>(
  rows: readonly (readonly C[])[],
  heightOf: (component: C) => number,
): (GridRect & { component: C })[] {
  let y = 0;
  return rows.flatMap((row) => {
    const spans = rowSpans(row.map((component) => component.span));
    const h = Math.max(1, ...row.map(heightOf));
    let x = 0;
    const items = row.map((component, index) => {
      const w = spans[index] ?? 1;
      // Its place is x/y/w/h now; the span it was saved with is dropped.
      const item = { x, y, w, h, component: { ...component, span: undefined } };
      x += w;
      return item;
    });
    y += h;
    return items;
  });
}
